/**
 * Shared list-entry builder used by:
 *   - web/app/c/list/[slug]/page.tsx  (server-rendered pSEO list page)
 *   - web/components/comparison/ComparisonStream.tsx (live search render)
 *
 * Mirrors api/app/services/pseo/list_builder.py::build_list_entries so
 * the rendered list count on the live search matches the server-side
 * quality-gate count on the published pSEO list page.
 *
 * Collapses SearchResult.groups into one entry per distinct
 * (brand, canonical_name) pair, sorted ascending by True Final Price.
 */

import type { ListCardEntry } from "@/components/comparison/v2/ListCard";
import type { EquivalenceGroup, ProductPageData } from "@/lib/api/types";

export interface PageEntry extends ListCardEntry {
  retailer_url: string;
  mpn: string | null;
}

/**
 * 2026-06-12: JS port of api/app/services/pseo/slug_builder.py::_kebabify.
 * NFKD-normalise unicode, drop non-ASCII bytes, lowercase, collapse
 * non-alphanumeric runs into single dashes, strip edge dashes.
 *
 * Used to compute a candidate /c/<slug> path for a list-card entry so
 * the list page can link directly to an existing pSEO page when one
 * exists, instead of routing through /compare?q=<name>.
 *
 * Returns empty string when the input is empty / non-ASCII-only.
 */
export function kebabifyForSlug(value: string): string {
  if (!value) return "";
  return value
    .normalize("NFKD")
    .replace(/[^\x00-\x7F]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * 2026-06-12: build a candidate product slug for a list-card entry.
 * Mirrors api/app/services/pseo/slug_builder.py::build_product_slug.
 *
 * Returns null when brand or mpn is missing / empty after kebabify so
 * the caller knows to fall back to /compare?q=<name>.
 */
export function buildCandidateProductSlug(
  brand: string | null | undefined,
  mpn: string | null | undefined,
  countryCode: string = "AU",
): string | null {
  const brandPart = kebabifyForSlug(brand || "");
  const mpnPart = kebabifyForSlug(mpn || "");
  const countryPart = kebabifyForSlug(countryCode);
  if (!brandPart || !mpnPart || !countryPart) return null;
  return `${brandPart}-${mpnPart}-${countryPart}`;
}

/**
 * 2026-06-13: filler tokens that the LLM intent parser sometimes
 * leaks into `constraints` even though they are already implied by
 * the "Best ..." heading the list page renders. A constraint that
 * consists ENTIRELY of these tokens is dropped; otherwise the tokens
 * are filtered out and the remainder is kept.
 *
 * Mirrors api/app/services/pseo/slug_builder.py::_FILLER_WORDS so
 * the same denylist applies to slug construction and constraint
 * rendering.
 */
const _CONSTRAINT_FILLER_TOKENS: ReadonlySet<string> = new Set([
  "best",
  "top",
  "top-rated",
  "rated",
  "cheapest",
  "cheap",
  "premium",
  "popular",
  "recommended",
  "good",
  "great",
  "value",
  "ultimate",
  "quality",
]);

/**
 * 2026-06-13: sanitise ONE LLM-emitted constraint string before
 * display. Strips parens and slashes (root cause of "(under $500,
 * best (top-rated / best value))" double-bracket renders), collapses
 * whitespace, drops marketing-filler tokens that duplicate the
 * "Best ..." heading. Returns null when the entire string is filler
 * or empty post-strip.
 *
 * Examples:
 *   "under $500"                        -> "under $500"
 *   "best (top-rated / best value)"     -> null (pure filler)
 *   "(over $100)"                       -> "over $100"
 *   "AU warranty only"                  -> "AU warranty only"
 *   "Top-rated brands"                  -> "brands"
 */
export function sanitiseConstraint(raw: string): string | null {
  if (!raw) return null;
  // Preserve original casing of substantive tokens; only filler
  // comparison is lowercase.
  const stripped = raw
    .replace(/[()]/g, " ")
    .replace(/[/\\]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!stripped) return null;
  const tokens = stripped.split(" ").filter(Boolean);
  const kept = tokens.filter(
    (t) => !_CONSTRAINT_FILLER_TOKENS.has(t.toLowerCase()),
  );
  if (kept.length === 0) return null;
  return kept.join(" ");
}

/**
 * 2026-06-13: sanitise + dedup a constraint array. Used by both the
 * /c/list/<slug> SSR heading builder and the ComparisonStream
 * live-search heading builder so a single helper defends both
 * surfaces against bad data already stored in
 * `public_comparison_slugs.parsed_intent` and against any future
 * LLM regressions that emit parens / slashes / filler tokens.
 */
export function sanitiseConstraints(
  constraints: ReadonlyArray<string> | null | undefined,
): string[] {
  if (!constraints || constraints.length === 0) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of constraints) {
    const clean = sanitiseConstraint(c);
    if (!clean) continue;
    const key = clean.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(clean);
  }
  return out;
}

export function buildListCardEntries(
  groups: EquivalenceGroup[],
  limit: number,
): PageEntry[] {
  const byKey = new Map<string, PageEntry>();
  for (const group of groups) {
    if (!group.products || group.products.length === 0) continue;
    const cheapest = pickCheapest(group.products);
    if (!cheapest) continue;
    const brand =
      group.canonical_brand ||
      cheapest.manufacturer_model_number ||
      "";
    const canonical_name = group.canonical_name || cheapest.product_name;
    const key = `${brand.toLowerCase()}|${(canonical_name || "").toLowerCase()}`;
    const retailer_count = new Set(
      group.products
        .map((p) => p.retailer_slug)
        .filter((s): s is string => Boolean(s)),
    ).size;
    const image_url = group.image_url ?? cheapest.image_url ?? null;
    const candidate: PageEntry = {
      position: 0,
      canonical_name,
      brand,
      image_url,
      delivered_total: cheapest.delivered_total,
      retailer_slug: cheapest.retailer_slug,
      retailer_count,
      retailer_url: cheapest.product_url,
      mpn: group.canonical_mpn ?? cheapest.manufacturer_model_number ?? null,
    };
    const existing = byKey.get(key);
    if (
      !existing ||
      parseFloat(candidate.delivered_total) <
        parseFloat(existing.delivered_total)
    ) {
      byKey.set(key, candidate);
    }
  }
  const sorted = Array.from(byKey.values()).sort(
    (a, b) =>
      parseFloat(a.delivered_total) - parseFloat(b.delivered_total),
  );
  return sorted.slice(0, limit).map((e, i) => ({ ...e, position: i + 1 }));
}

export function pickCheapest(
  products: ProductPageData[],
): ProductPageData | null {
  let best: ProductPageData | null = null;
  for (const p of products) {
    if (!p.delivered_total) continue;
    const n = parseFloat(p.delivered_total);
    if (Number.isNaN(n)) continue;
    if (!best || n < parseFloat(best.delivered_total)) {
      best = p;
    }
  }
  return best;
}
